import { apolloAdapter } from '@warehouse/shared/infra';
import { firstValueFrom, map, Observable } from 'rxjs';
import {
	CREATE_TITLE_V2,
	DELETE_TITLE,
	EDIT_TITLE_V2,
	EXPORT_TITLE,
	GET_POSTER,
	GET_TITLE_V2,
	GetPosterOutput,
	GetTitleOutput,
	IMPORT_MEC,
} from '@warehouse/graphql';
import {
	CreateTitleInput,
	CreateTitleResult,
	EditTitleInput,
	ExportMECInput,
	PollOptions,
	QueryOptions,
	RelationshipType,
	TitleFull,
	TitleRepository,
} from '@warehouse/title/core';
import { singletonFactory } from '@warehouse/shared/util';
import { Title } from '@nexspec/warehouse-shared-types';
import { RelationshipFilters } from '@warehouse/relationships/domain';
import { WorkType } from '@warehouse/title-shared/core';
import { Poster } from '../core/models/poster';
import { posterGQLResponseToPoster } from './poster';
import { titleGQLResponseToTitleFull } from './title';
import { SEARCH_CHILDREN_RELATIONSHIP_TYPES } from '../../../src/graphql/queries/relationships';
import { formatFilters } from '../../../src/utils/hooks/titles/useSearchTitlesV2';
import { ChildrenRelationships } from '../core/children-relationship';

export const POLL_INTERVAL = 500;

export class GqlTitleRepository implements TitleRepository {
	private _apolloAdapter = apolloAdapter;

	watchTitle(uuid: string, { pollInterval }: PollOptions = {}): Observable<TitleFull> {
		const response$ = this._apolloAdapter.watchObservableQuery<{ getTitleV2: GetTitleOutput }>({
			query: GET_TITLE_V2,
			variables: {
				uuid,
			},
			pollInterval,
		});

		return response$.pipe(
			map((apolloResponse) =>
				titleGQLResponseToTitleFull({
					...apolloResponse.data.getTitleV2,
					state: JSON.parse(apolloResponse.data.getTitleV2.state) as Title,
				}),
			),
		);
	}

	getTitle(uuid: string): Promise<TitleFull> {
		return firstValueFrom(this.watchTitle(uuid));
	}

	async refetchTitle(uuid: string, options: QueryOptions = {}) {
		await this._apolloAdapter.client.query<{ getTitleV2: GetTitleOutput }>({
			query: GET_TITLE_V2,
			variables: {
				uuid,
			},
			context: {
				fetchOptions: {
					signal: options.signal,
				},
			},
			fetchPolicy: 'network-only',
		});
	}

	async editTitle({ type, uuid, edits }: EditTitleInput, options: { signal?: AbortSignal } = {}): Promise<TitleFull> {
		const mutateResponse = await this._apolloAdapter.client.mutate({
			mutation: EDIT_TITLE_V2,
			variables: {
				input: {
					edits: edits.map((edit) => ({
						...edit,
						value: edit.value ?? 'null',
					})),
					type,
					uuid,
				},
			},
			context: {
				fetchOptions: {
					signal: options.signal,
				},
			},
			update(cache, { data }) {
				try {
					if (!data?.editTitleV2) return;

					const title = data?.editTitleV2;

					cache.writeQuery({
						query: GET_TITLE_V2,
						variables: {
							uuid: title?.uuid,
						},
						data: {
							getTitleV2: title,
						},
					});
				} catch (error) {
					console.error(error);
				}
			},
		});

		// Hypothesis: It should never throw here, because the mutation should always return data
		// In case of errors (abort,  errors in mutation, ...), the mutate function should throw
		// We do not understand why the mutation would not return data
		if (!mutateResponse.data?.editTitleV2) throw new Error('No data returned from editTitle mutation');

		const { indexed, state, ultraMezzUuid } = mutateResponse.data.editTitleV2;

		const title = JSON.parse(state) as Title;

		return titleGQLResponseToTitleFull({ indexed, state: title, ultraMezzUuid, uuid });
	}

	// eslint-disable-next-line class-methods-use-this
	private _gqlOutputToTitleFull({ indexed, state, ultraMezzUuid, uuid }: GetTitleOutput): TitleFull {
		return titleGQLResponseToTitleFull({ indexed, state: JSON.parse(state) as Title, ultraMezzUuid, uuid });
	}

	async createTitle(
		{ metadata, dryRun = false, primaryEdit = undefined }: CreateTitleInput,
		options: QueryOptions = {},
	): Promise<CreateTitleResult> {
		const mutateResponse = await this._apolloAdapter.client.mutate<{
			createTitleV2: GetTitleOutput & { title: GetTitleOutput; edit?: GetTitleOutput };
		}>({
			mutation: CREATE_TITLE_V2,
			variables: {
				input: {
					dryRun,
					metadata,
					primaryEdit,
				},
			},
			context: {
				fetchOptions: {
					signal: options.signal,
				},
				dryRun,
			},
			update(cache, { data }, { context }) {
				if (context?.dryRun) return;

				try {
					const result = data?.createTitleV2;
					if (!result) return;

					[result.title].forEach((titleOutput) => {
						if (!titleOutput?.uuid) return;
						cache.writeQuery({
							query: GET_TITLE_V2,
							variables: {
								uuid: titleOutput.uuid,
							},
							data: {
								getTitleV2: titleOutput,
							},
						});
					});
				} catch (error) {
					console.error(error);
				}
			},
		});

		if (!mutateResponse.data?.createTitleV2?.title) throw new Error('No data returned from createTitle mutation');


    
    const { title: titleOutput, edit } = mutateResponse.data.createTitleV2;
		const title = this._gqlOutputToTitleFull(titleOutput);

		return {  
			title,
			edit: edit ? this._gqlOutputToTitleFull(edit) : undefined,
			metadata: title.metadata,
			ultraMezzUuid: title.ultraMezzUuid,
			uuid: title.uuid,
			readonly: title.readonly,
		};
	}

	watchPoster(uuid: string, { pollInterval }: { pollInterval?: number } = {}): Observable<Poster> {
		const response$ = this._apolloAdapter.watchObservableQuery<{ getPoster: GetPosterOutput }>({
			query: GET_POSTER,
			variables: {
				uuid,
			},
			pollInterval,
		});

		return response$.pipe(map((apolloResponse) => posterGQLResponseToPoster(apolloResponse.data.getPoster)));
	}

	getPoster(uuid: string): Promise<Poster> {
		return firstValueFrom(this.watchPoster(uuid));
	}

	async refetchPoster(uuid: string, options: QueryOptions = {}): Promise<void> {
		await this._apolloAdapter.client.query<{ getPoster: GetPosterOutput }>({
			query: GET_POSTER,
			variables: {
				uuid,
			},
			context: {
				fetchOptions: {
					signal: options.signal,
				},
			},
			fetchPolicy: 'network-only',
		});
	}

	async importMEC(mec: string): Promise<TitleFull> {
		const mutateResponse = await this._apolloAdapter.client.mutate({
			mutation: IMPORT_MEC,
			variables: {
				input: {
					mec,
				},
			},
			update(cache, { data }) {
				try {
					if (!data?.importMEC) return;

					const title = data?.importMEC;

					cache.writeQuery({
						query: GET_TITLE_V2,
						variables: {
							uuid: title?.uuid,
						},
						data: {
							getTitleV2: title,
						},
					});
				} catch (error) {
					console.error(error);
					throw new Error((error as Error).message || 'Failed to import MEC');
				}
			},
		});

		if (!mutateResponse.data?.importMEC) throw new Error('No data returned from importMEC mutation');

		const { indexed, state, ultraMezzUuid, uuid } = mutateResponse.data.importMEC;

		const title = JSON.parse(state) as Title;

		return titleGQLResponseToTitleFull({ indexed, state: title, ultraMezzUuid, uuid });
	}

	async exportTitleMEC({ uuid, type }: ExportMECInput, options: QueryOptions = {}): Promise<string> {
		const queryResponse = await this._apolloAdapter.client.query({
			query: EXPORT_TITLE,
			variables: {
				input: {
					uuid,
					type,
				},
			},
			context: {
				fetchOptions: {
					signal: options.signal,
				},
			},
			fetchPolicy: 'no-cache',
		});

		if (!queryResponse.data?.exportTitle) throw new Error('No data returned from exportTitle query');

		return queryResponse.data.exportTitle.mec;
	}

	async deleteTitle(uuid: string): Promise<string> {
		const { data } = await this._apolloAdapter.client.mutate({
			mutation: DELETE_TITLE,
			variables: { input: { uuid } },
		});

		if (!data?.deleteTitle) throw new Error('No data returned from deleteTitle mutation');

		return data.deleteTitle;
	}

	watchChildrenRelationshipsTypes(uuid: string): Observable<ChildrenRelationships> {
		const buildSearch = (relationshipType?: string, includedWorkTypes?: WorkType[]) => {
			const filters = RelationshipFilters.childrenOf({ titleUuid: uuid, relationshipType, includedWorkTypes });
			return {
				pagination: { page: 1, perPage: 1 },
				filters: JSON.stringify(formatFilters(filters)),
			};
		};

		type AliasedResult = Record<string, { documents: { relationship: { type: string } }[] }>;

		const response$ = this._apolloAdapter.watchObservableQuery<AliasedResult>({
			query: SEARCH_CHILDREN_RELATIONSHIP_TYPES,
			variables: {
				seasons: buildSearch(RelationshipType.SeasonOf),
				episodes: buildSearch(RelationshipType.EpisodeOf),
				entries: buildSearch(RelationshipType.EntryOf),
				edits: buildSearch(RelationshipType.DerivedFrom, [WorkType.Edit]),
				manifestations: buildSearch(RelationshipType.DerivedFrom, [WorkType.Manifestation]),
			},
			pollInterval: POLL_INTERVAL,
		});

		return response$.pipe(
			map((apolloResponse) => ({
				seasons: (apolloResponse.data.seasons?.documents ?? []).length > 0,
				episodes: (apolloResponse.data.episodes?.documents ?? []).length > 0,
				entries: (apolloResponse.data.entries?.documents ?? []).length > 0,
				edits: (apolloResponse.data.edits?.documents ?? []).length > 0,
				manifestations: (apolloResponse.data.manifestations?.documents ?? []).length > 0,
			})),
		);
	}
}

export const titleRepositorySingleton = singletonFactory<TitleRepository>({ factory: () => new GqlTitleRepository() });
